/**
 * Campaign List V2 bulk daily-budget rules (min budget + decimal scale).
 * Prefer currencyCode from clientData → amazon_setup.profile_info;
 * fall back to countryCode when currency is unknown.
 *
 * Mins = Amazon hard floor (not marketing recommended budgets).
 * Scale = 2 decimals for all currencies (for now).
 */

export const CAMPAIGN_LIST_V2_DEFAULT_CURRENCY_CODE = "INR";
export const CAMPAIGN_LIST_V2_DEFAULT_FRACTION_DIGITS = 2;

/**
 * Minimum daily budget by marketplace currency (Amazon Ads hard floor).
 */
export const CAMPAIGN_LIST_V2_CURRENCY_BUDGET_RULES = {
  USD: { minBudget: 1, scale: 2 },
  CAD: { minBudget: 1, scale: 2 },
  GBP: { minBudget: 1, scale: 2 },
  EUR: { minBudget: 1, scale: 2 },
  INR: { minBudget: 50, scale: 2 },
  JPY: { minBudget: 100, scale: 2 },
  AUD: { minBudget: 1, scale: 2 },
  MXN: { minBudget: 1, scale: 2 },
  AED: { minBudget: 4, scale: 2 },
  BRL: { minBudget: 1, scale: 2 },
  SGD: { minBudget: 4, scale: 2 },
  SEK: { minBudget: 1, scale: 2 },
  EGP: { minBudget: 7, scale: 2 },
  PLN: { minBudget: 1, scale: 2 },
  SAR: { minBudget: 4, scale: 2 },
  TRY: { minBudget: 2, scale: 2 },
  ZAR: { minBudget: 6, scale: 2 },
};

/** countryCode → currencyCode when currency is missing / unrecognized */
export const CAMPAIGN_LIST_V2_COUNTRY_TO_CURRENCY = {
  US: "USD",
  CA: "CAD",
  UK: "GBP",
  GB: "GBP",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  IN: "INR",
  JP: "JPY",
  AU: "AUD",
  MX: "MXN",
  AE: "AED",
  UAE: "AED",
  BR: "BRL",
  SG: "SGD",
  SE: "SEK",
  EG: "EGP",
  PL: "PLN",
  SA: "SAR",
  TR: "TRY",
  ZA: "ZAR",
};

function readAmazonProfileInfo() {
  try {
    const clientData = JSON.parse(localStorage.getItem("clientData") || "{}");
    return clientData?.amazon_setup?.profile_info || {};
  } catch {
    return {};
  }
}

export function getAmazonCurrencyCodeFromClientData() {
  const profile = readAmazonProfileInfo();
  const code = profile?.currencyCode;
  if (typeof code === "string" && code.trim()) {
    return code.trim().toUpperCase();
  }
  return null;
}

export function getAmazonCountryCodeFromClientData() {
  const profile = readAmazonProfileInfo();
  const code = profile?.countryCode;
  if (typeof code === "string" && code.trim()) {
    return code.trim().toUpperCase();
  }
  return null;
}

export function resolveCampaignListCurrencyCode(currencyCode, countryCode) {
  const currency = String(
    currencyCode || getAmazonCurrencyCodeFromClientData() || ""
  )
    .trim()
    .toUpperCase();

  if (
    currency &&
    Object.prototype.hasOwnProperty.call(
      CAMPAIGN_LIST_V2_CURRENCY_BUDGET_RULES,
      currency
    )
  ) {
    return currency;
  }

  const country = String(
    countryCode || getAmazonCountryCodeFromClientData() || ""
  )
    .trim()
    .toUpperCase();
  const mapped = CAMPAIGN_LIST_V2_COUNTRY_TO_CURRENCY[country];
  if (
    mapped &&
    Object.prototype.hasOwnProperty.call(
      CAMPAIGN_LIST_V2_CURRENCY_BUDGET_RULES,
      mapped
    )
  ) {
    return mapped;
  }

  return CAMPAIGN_LIST_V2_DEFAULT_CURRENCY_CODE;
}

export function getCampaignListBudgetRule(currencyCode, countryCode) {
  const resolved = resolveCampaignListCurrencyCode(currencyCode, countryCode);
  return {
    currencyCode: resolved,
    ...CAMPAIGN_LIST_V2_CURRENCY_BUDGET_RULES[resolved],
  };
}

export function getCampaignListMinBudget(currencyCode, countryCode) {
  return getCampaignListBudgetRule(currencyCode, countryCode).minBudget;
}

export function roundCampaignListBudget(
  value,
  currencyCode,
  countryCode
) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const { scale } = getCampaignListBudgetRule(currencyCode, countryCode);
  if (scale === 0) return Math.round(numeric);
  const factor = 10 ** scale;
  return Math.round(numeric * factor) / factor;
}

/**
 * Rules object for BulkAdjustBidModalV2 (Campaign List V2 budget only).
 * Uses minBid key so the shared modal clamp/validation path works unchanged.
 */
export function getCampaignListV2BudgetRules(
  currencyCode = getAmazonCurrencyCodeFromClientData(),
  countryCode = getAmazonCountryCodeFromClientData()
) {
  const rule = getCampaignListBudgetRule(currencyCode, countryCode);
  return {
    currencyCode: rule.currencyCode,
    unit: rule.currencyCode,
    minBid: rule.minBudget,
    fractionDigits: rule.scale ?? CAMPAIGN_LIST_V2_DEFAULT_FRACTION_DIGITS,
    roundBid: (value) =>
      roundCampaignListBudget(value, rule.currencyCode, countryCode),
  };
}
